import {
  useCreatePaymentOrderMutation,
  useReleasePaymentReservationMutation,
} from "@/lib/api/apiSlice";
import { isNativePlatform, startNativeCashfreeCheckout } from "@/lib/capacitor";
import { getRtkQueryErrorMessage } from "@/lib/error";
import { getPaymentReturnUrl } from "@/lib/payment-return-url";
import {
  ICartItem,
  ICreatePaymentOrderBody,
  OrderDeliveryType,
  IUserAddress,
} from "muminsmart-utils";
import { ReactElement, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface CheckoutRenderProps {
  handleCheckout: () => void;
  isCheckoutLoading: boolean;
}

type CheckoutChildren = (props: CheckoutRenderProps) => ReactElement;

interface CheckoutProps {
  items: ICartItem[];
  selectedAddress: IUserAddress | null;
  couponCode?: string;
  eligibleQuantity?: number;
  eligibleUnitPrice?: number;
  sellerDeliveryTypes?: Record<string, OrderDeliveryType>;
  sellerRemarks?: Record<string, string>;
  onPaymentSuccess?: () => void;
  onOutOfStock?: (productVariantIds: string[]) => void;
  children: CheckoutChildren;
}

function getOutOfStockProductVariantIds(error: unknown): string[] {
  if (!error || typeof error !== "object" || !("data" in error)) {
    return [];
  }

  const data = (error as { data: unknown }).data;

  if (!data || typeof data !== "object" || !("productVariantIds" in data)) {
    return [];
  }

  const productVariantIds = (data as { productVariantIds: unknown })
    .productVariantIds;

  if (!Array.isArray(productVariantIds)) {
    return [];
  }

  return productVariantIds.filter((id): id is string => typeof id === "string");
}

type CashfreeInstance = Awaited<
  ReturnType<typeof import("@cashfreepayments/cashfree-js").load>
>;

const Checkout = ({
  items,
  selectedAddress,
  couponCode,
  eligibleQuantity,
  eligibleUnitPrice,
  sellerDeliveryTypes,
  sellerRemarks,
  onOutOfStock,
  children,
}: CheckoutProps) => {
  const navigate = useNavigate();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [createPaymentOrder] = useCreatePaymentOrderMutation();
  const [releasePaymentReservation] = useReleasePaymentReservationMutation();
  const cashfreeRef = useRef<CashfreeInstance | null>(null);
  const cashfreePromiseRef = useRef<Promise<CashfreeInstance> | null>(null);

  const loadCashfree = async (): Promise<CashfreeInstance> => {
    if (cashfreeRef.current) {
      return cashfreeRef.current;
    }

    if (!cashfreePromiseRef.current) {
      cashfreePromiseRef.current = import("@cashfreepayments/cashfree-js")
        .then(({ load }) =>
          load({
            mode:
              process.env.NODE_ENV === "production" ? "production" : "sandbox",
          }),
        )
        .then((instance) => {
          cashfreeRef.current = instance;
          return instance;
        })
        .catch((error) => {
          cashfreePromiseRef.current = null;
          throw error;
        });
    }

    return cashfreePromiseRef.current;
  };

  const handleCheckout = async () => {
    if (isCheckoutLoading) {
      return;
    }

    if (!items.length) {
      toast.error("Cart is empty");
      return;
    }

    if (!selectedAddress?.id) {
      toast.error("Please select a delivery address");
      return;
    }

    setIsCheckoutLoading(true);

    let attemptId: string | null = null;
    const releaseReservation = async (paymentAttemptId: string) => {
      try {
        await releasePaymentReservation({ paymentAttemptId }).unwrap();
      } catch {
        // Best-effort cleanup; server TTL handles stale reservations.
      }
    };

    try {
      const createPaymentPayload: ICreatePaymentOrderBody = {
        addressId: selectedAddress.id,
        couponCode,
        eligibleQuantity,
        eligibleUnitPrice,
        sellerDeliveryTypes,
        sellerRemarks: Object.fromEntries(
          Object.entries(sellerRemarks ?? {}).flatMap(([sellerId, remark]) => {
            const trimmedRemark = remark.trim();
            return trimmedRemark ? [[sellerId, trimmedRemark]] : [];
          }),
        ),
      };

      const paymentData =
        await createPaymentOrder(createPaymentPayload).unwrap();
      attemptId = paymentData.paymentAttemptId;

      if (isNativePlatform()) {
        try {
          const result = await startNativeCashfreeCheckout({
            orderId: paymentData.orderId,
            paymentSessionId: paymentData.paymentSessionId,
            paymentEnvironment: paymentData.paymentEnvironment,
          });

          if (result.error) {
            void releaseReservation(paymentData.paymentAttemptId);
            toast.error("Unable to complete payment. Please try again.");
            setIsCheckoutLoading(false);
            return;
          }

          navigate(
            `/payment/success?paymentAttemptId=${encodeURIComponent(paymentData.paymentAttemptId)}&orderId=${encodeURIComponent(paymentData.orderId)}`,
            { replace: true },
          );
          setIsCheckoutLoading(false);
          return;
        } catch (error) {
          void releaseReservation(paymentData.paymentAttemptId);
          console.error("Native Cashfree checkout failed", error);
          toast.error("Unable to start payment. Please try again.");
          setIsCheckoutLoading(false);
          return;
        }
      }

      let cashfreeInstance: CashfreeInstance;
      try {
        cashfreeInstance = await loadCashfree();
      } catch (error) {
        console.error("Failed to initialize Cashfree SDK", error);
        toast.error("Unable to load payment provider. Please try again.");
        setIsCheckoutLoading(false);
        return;
      }

      if (!cashfreeInstance) {
        toast.error("Unable to load payment provider. Please try again.");
        setIsCheckoutLoading(false);
        return;
      }

      const returnUrl = getPaymentReturnUrl(
        paymentData.paymentAttemptId,
        paymentData.orderId,
      );

      try {
        const result = await cashfreeInstance.checkout({
          paymentSessionId: paymentData.paymentSessionId,
          returnUrl: returnUrl.toString(),
          redirectTarget: "_self",
        });

        if (result?.error) {
          void releaseReservation(paymentData.paymentAttemptId);
          toast.error(result.error.message || "Unable to start payment");
          setIsCheckoutLoading(false);
          return;
        }
      } catch (error) {
        void releaseReservation(attemptId ?? "");
        console.error("Cashfree checkout failed", error);
        toast.error("Unable to start payment. Please try again.");
        setIsCheckoutLoading(false);
      }
    } catch (error: unknown) {
      void releaseReservation(attemptId ?? "");
      const productVariantIds = getOutOfStockProductVariantIds(error);

      if (productVariantIds.length) {
        onOutOfStock?.(productVariantIds);
      }

      toast.error(
        getRtkQueryErrorMessage(
          error,
          "Unable to start payment. Please try again.",
        ),
      );
      setIsCheckoutLoading(false);
    }
  };

  return (
    <>
      {children({
        handleCheckout: () => void handleCheckout(),
        isCheckoutLoading,
      })}
    </>
  );
};

export default Checkout;
